import { useEffect, useRef, useState } from 'react';
import { serviceAreas } from '../../mocks/homeData';

export default function ContactSection() {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [charCount, setCharCount] = useState(0);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const handleSubmit = () => {
    setFormStatus('submitting');
    setTimeout(() => {
      setFormStatus('success');
      setTimeout(() => setFormStatus('idle'), 5000);
    }, 1500);
  };

  const handleTextareaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setCharCount(e.target.value.length);
  };

  return (
    <section id="contact" ref={sectionRef} className="relative py-16 md:py-24 overflow-hidden bg-teal-50">
      <div className="relative z-10 w-full px-4 md:px-8 lg:px-12">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-start">
            <div
              className={`transition-all duration-700 ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
            >
              <h2 className="font-heading font-extrabold text-charcoal text-3xl md:text-4xl lg:text-5xl uppercase tracking-tight">
                Get Your Free Quote
              </h2>
              <p className="font-body text-charcoal/70 text-base md:text-lg mt-3">
                Transform Your Space Today
              </p>

              <div className="mt-8 space-y-3">
                {[
                  'Free Consultation & Estimates',
                  'Licensed, Bonded & Insured',
                  'Serving All South Florida',
                ].map((item) => (
                  <div key={item} className="flex items-center gap-3">
                    <div className="w-5 h-5 flex items-center justify-center">
                      <i className="ri-check-line text-teal"></i>
                    </div>
                    <span className="font-body text-charcoal/80 text-sm">{item}</span>
                  </div>
                ))}
              </div>

              <div className="mt-10 space-y-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-teal/20">
                    <i className="ri-phone-line text-teal"></i>
                  </div>
                  <div>
                    <p className="font-body text-charcoal/50 text-xs">Call Us</p>
                    <p className="font-heading font-semibold text-charcoal text-base">(954) 560-2181</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-teal/20">
                    <i className="ri-mail-line text-teal"></i>
                  </div>
                  <div>
                    <p className="font-body text-charcoal/50 text-xs">Email Us</p>
                    <a href="mailto:contact@ariaproperty.net" className="font-heading font-semibold text-charcoal text-base hover:text-teal transition-colors">contact@ariaproperty.net</a>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-teal/20">
                    <i className="ri-map-pin-line text-teal"></i>
                  </div>
                  <div>
                    <p className="font-body text-charcoal/50 text-xs">Service Areas</p>
                    <p className="font-body text-charcoal text-sm">{serviceAreas.join(', ')}</p>
                  </div>
                </div>
              </div>

              <div className="mt-8 flex items-center gap-2">
                <div className="flex">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <div key={star} className="w-5 h-5 flex items-center justify-center">
                      <i className="ri-star-fill text-teal text-sm"></i>
                    </div>
                  ))}
                </div>
                <span className="font-body text-charcoal/70 text-sm">Rated 4.9/5 Stars</span>
              </div>
            </div>

            <div
              className={`transition-all duration-700 delay-200 ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
            >
              <div className="bg-white rounded-lg p-6 md:p-8 shadow-sm border border-concrete">
                {formStatus === 'success' ? (
                  <div className="text-center py-8">
                    <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-teal-100 mb-4">
                      <i className="ri-check-line text-teal text-2xl"></i>
                    </div>
                    <h3 className="font-heading font-bold text-charcoal text-xl mb-2">Quote Request Sent!</h3>
                    <p className="font-body text-charcoal/70 text-sm">
                      Thank you for reaching out. We'll get back to you within 24 hours with your free estimate.
                    </p>
                  </div>
                ) : (
                  <form
                    id="quote-form"
                    data-readdy-form
                    action="https://readdy.ai/api/form/d7rrar4dj3knvn2p2lgg"
                    method="POST"
                    encType="application/x-www-form-urlencoded"
                    onSubmit={(e) => {
                      e.preventDefault();
                      const form = e.currentTarget;
                      const formData = new FormData(form);
                      const textarea = form.querySelector('textarea') as HTMLTextAreaElement;
                      if (textarea && textarea.value.length > 500) {
                        alert('Project description must be 500 characters or less.');
                        return;
                      }
                      fetch(form.action, {
                        method: 'POST',
                        body: new URLSearchParams(formData as unknown as Record<string, string>),
                        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                      })
                        .then(() => handleSubmit())
                        .catch(() => setFormStatus('error'));
                    }}
                  >
                    <div className="space-y-4">
                      <div>
                        <label className="font-body text-charcoal text-sm font-semibold block mb-1.5">Full Name</label>
                        <input
                          type="text"
                          name="full_name"
                          required
                          placeholder="John Smith"
                          className="w-full px-4 py-3 border border-concrete rounded-md font-body text-sm text-charcoal placeholder:text-concrete focus:outline-none focus:border-teal transition-colors"
                        />
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label className="font-body text-charcoal text-sm font-semibold block mb-1.5">Phone</label>
                          <input
                            type="tel"
                            name="phone"
                            required
                            placeholder="(954) 560-2181"
                            className="w-full px-4 py-3 border border-concrete rounded-md font-body text-sm text-charcoal placeholder:text-concrete focus:outline-none focus:border-teal transition-colors"
                          />
                        </div>
                        <div>
                          <label className="font-body text-charcoal text-sm font-semibold block mb-1.5">Email</label>
                          <input
                            type="email"
                            name="email"
                            required
                            placeholder="john@example.com"
                            className="w-full px-4 py-3 border border-concrete rounded-md font-body text-sm text-charcoal placeholder:text-concrete focus:outline-none focus:border-teal transition-colors"
                          />
                        </div>
                      </div>

                      <div>
                        <label className="font-body text-charcoal text-sm font-semibold block mb-1.5">Service Type</label>
                        <select
                          name="service_type"
                          required
                          className="w-full px-4 py-3 border border-concrete rounded-md font-body text-sm text-charcoal bg-white focus:outline-none focus:border-teal transition-colors appearance-none"
                          style={{ backgroundImage: `none` }}
                        >
                          <option value="">Select a service...</option>
                          <option value="demolition">Demolition Services</option>
                          <option value="reconstruction">Full Reconstruction</option>
                          <option value="kitchen">Kitchen Remodeling</option>
                          <option value="bathroom">Bathroom Renovation</option>
                          <option value="handyman">General Handyman</option>
                          <option value="emergency">Emergency Repairs</option>
                          <option value="other">Other / Not Sure</option>
                        </select>
                      </div>

                      <div>
                        <label className="font-body text-charcoal text-sm font-semibold block mb-1.5">Project Address</label>
                        <input
                          type="text"
                          name="project_address"
                          required
                          placeholder="123 Main St, Miami, FL"
                          className="w-full px-4 py-3 border border-concrete rounded-md font-body text-sm text-charcoal placeholder:text-concrete focus:outline-none focus:border-teal transition-colors"
                        />
                      </div>

                      <div>
                        <label className="font-body text-charcoal text-sm font-semibold block mb-1.5">
                          Project Description
                          <span className="font-normal text-concrete ml-1">({charCount}/500)</span>
                        </label>
                        <textarea
                          name="project_description"
                          rows={4}
                          maxLength={500}
                          placeholder="Tell us about your project..."
                          onChange={handleTextareaChange}
                          className="w-full px-4 py-3 border border-concrete rounded-md font-body text-sm text-charcoal placeholder:text-concrete focus:outline-none focus:border-teal transition-colors resize-none"
                        ></textarea>
                      </div>

                      <div>
                        <label className="font-body text-charcoal text-sm font-semibold block mb-2">Preferred Contact Method</label>
                        <div className="flex flex-wrap gap-4">
                          {['Phone', 'Email', 'Text'].map((method) => (
                            <label key={method} className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="radio"
                                name="contact_method"
                                value={method.toLowerCase()}
                                defaultChecked={method === 'Phone'}
                                className="w-4 h-4 accent-teal"
                              />
                              <span className="font-body text-charcoal text-sm">{method}</span>
                            </label>
                          ))}
                        </div>
                      </div>

                      <button
                        type="submit"
                        disabled={formStatus === 'submitting'}
                        className="w-full bg-teal hover:bg-teal-dark text-white font-heading font-semibold text-sm uppercase tracking-wider px-8 py-4 rounded-md transition-colors flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer disabled:opacity-70"
                      >
                        {formStatus === 'submitting' ? (
                          <>
                            <div className="w-5 h-5 flex items-center justify-center">
                              <i className="ri-loader-4-line animate-spin"></i>
                            </div>
                            Sending...
                          </>
                        ) : (
                          <>
                            <div className="w-5 h-5 flex items-center justify-center">
                              <i className="ri-send-plane-line"></i>
                            </div>
                            Request Free Quote
                          </>
                        )}
                      </button>

                      <p className="font-body text-concrete text-xs text-center">
                        Your information is secure and confidential.
                      </p>
                    </div>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}